import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Snowflake, Truck, ShieldCheck, Sparkles, Building2 } from "lucide-react";
import { api } from "../lib/api";
import ProductCard from "../components/ProductCard";
import PartnerRequestDialog from "../components/PartnerRequestDialog";

const Home = () => {
  const [products, setProducts] = useState([]);
  useEffect(() => {
    api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 frost-grain pointer-events-none" />
        <div className="absolute -top-32 -right-40 w-[500px] h-[500px] rounded-full bg-sky-100/60 blur-3xl pointer-events-none" />
        <div className="absolute -bottom-32 -left-40 w-[500px] h-[500px] rounded-full bg-cyan-50/80 blur-3xl pointer-events-none" />

        <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-16 pb-20 lg:pt-24 lg:pb-28 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center relative">
          <div className="lg:col-span-6 fade-up">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-sky-50 border border-sky-100 mb-6">
              <Snowflake className="w-3.5 h-3.5 text-sky-600" />
              <span className="text-xs tracking-[0.2em] uppercase font-bold text-sky-700">In Calabria · dal 2018</span>
            </div>
            <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl tracking-tight font-light text-[#032B44] leading-[1.05]">
              Ghiaccio puro<br/>
              <span className="text-[#00A3FF] font-medium">e certificato.</span>
            </h1>
            <p className="mt-6 text-lg text-slate-600 max-w-lg leading-relaxed">
              Cubetti da 22 gr e ghiaccio pilé di qualità certificata per bar,
              ristoranti, eventi e privati con consegna express in tutta la Calabria.
            </p>

            <div className="mt-10 flex flex-wrap items-center gap-4">
              <Link to="/catalog" className="group inline-flex items-center gap-2 h-12 px-7 rounded-lg bg-[#032B44] hover:bg-[#00A3FF] text-white font-medium transition-colors" data-testid="hero-shop-button">
                Esplora i prodotti
                <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
              </Link>
              <a href="#products" className="inline-flex items-center gap-2 h-12 px-7 rounded-lg border border-sky-200 text-[#032B44] hover:bg-sky-50 font-medium transition-colors" data-testid="hero-learn-button">
                Come funziona
              </a>
            </div>

            <div className="mt-12 grid grid-cols-3 gap-6 max-w-md">
              <Stat n="500+" label="Clienti business" />
              <Stat n="24h" label="Consegna express" />
              <Stat n="100%" label="Acqua filtrata e certificata" />
            </div>
          </div>

          <div className="lg:col-span-6 relative">
            <div className="relative aspect-[4/5] rounded-3xl overflow-hidden border border-sky-100">
              <img
                src="/generated/hero-ice-cubes.jpg"
                alt="Cubetti di ghiaccio trasparenti EasyIce"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#032B44]/40 via-transparent to-transparent" />
              <div className="absolute bottom-6 left-6 right-6 glass rounded-2xl p-5 border border-white/40">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-[#00A3FF] grid place-items-center">
                    <Sparkles className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="text-xs tracking-[0.2em] uppercase font-bold text-sky-600">Nuovo</p>
                    <p className="text-sm font-medium text-[#032B44]">Listini personalizzati per partner</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="hidden lg:block absolute -bottom-6 -left-6 bg-white rounded-2xl border border-sky-100 p-5 shadow-xl float-slow">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-sky-50 grid place-items-center">
                  <Truck className="w-5 h-5 text-sky-700" />
                </div>
                <div>
                  <p className="text-[11px] tracking-[0.2em] uppercase font-bold text-sky-600">Consegna</p>
                  <p className="text-sm font-medium text-[#032B44]">Tutta la Calabria</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="border-y border-sky-100 bg-sky-50/40">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-14 grid grid-cols-1 md:grid-cols-3 gap-8">
          <Feature icon={<Snowflake />} title="Purezza certificata" desc="Acqua filtrata a 3 stadi, congelamento lento per un ghiaccio trasparente e a lento scioglimento." />
          <Feature icon={<Truck />} title="Logistica refrigerata" desc="Mezzi a temperatura controllata, consegna programmata o express in giornata." />
          <Feature icon={<ShieldCheck />} title="Listini personalizzati" desc="Per bar, ristoranti e catene: ricevi un prezzo dedicato visibile dopo l'accesso." />
        </div>
      </section>

      {/* Products */}
      <section id="products" className="py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="flex items-end justify-between mb-12 gap-6 flex-wrap">
            <div>
              <p className="text-xs tracking-[0.2em] uppercase font-bold text-sky-600 mb-3">Catalogo</p>
              <h2 className="font-heading text-4xl sm:text-5xl tracking-tight font-light text-[#032B44]">
                Ogni forma di ghiaccio,<br/>per ogni esigenza.
              </h2>
            </div>
            <Link to="/catalog" className="text-[#00A3FF] font-medium inline-flex items-center gap-1.5 group" data-testid="view-all-products">
              Tutti i prodotti
              <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
            </Link>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-8 max-w-3xl">
            {products.slice(0, 6).map((p) => (
              <ProductCard key={p.product_id} product={p} />
            ))}
          </div>
        </div>
      </section>

      {/* Partner CTA */}
      <section className="py-24 bg-gradient-to-br from-[#032B44] via-[#063e60] to-[#00A3FF] text-white relative overflow-hidden">
        <div className="absolute -top-32 -right-20 w-[400px] h-[400px] rounded-full bg-cyan-300/20 blur-3xl pointer-events-none" />
        <div className="absolute -bottom-32 -left-20 w-[400px] h-[400px] rounded-full bg-white/10 blur-3xl pointer-events-none" />
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center relative">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 border border-white/20 mb-6">
              <Building2 className="w-3.5 h-3.5" />
              <span className="text-xs tracking-[0.2em] uppercase font-bold">Per attività</span>
            </div>
            <h2 className="font-heading text-4xl sm:text-5xl tracking-tight font-light leading-[1.1]">
              Bar, ristoranti, hotel?<br/>
              <span className="font-medium">Ottieni il tuo listino dedicato.</span>
            </h2>
            <p className="mt-5 text-base text-sky-100/90 max-w-md leading-relaxed">
              Inviaci la tua P.IVA e i volumi stimati: riceverai prezzi personalizzati,
              consegne programmate e supporto dedicato. Risposta entro 24 ore.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <PartnerRequestDialog
                trigger={
                  <button className="inline-flex items-center gap-2 h-12 px-7 rounded-lg bg-white text-[#032B44] font-medium hover:bg-sky-50 transition-colors" data-testid="partner-cta-button">
                    Richiedi listino partner <ArrowRight className="w-4 h-4" />
                  </button>
                }
              />
              <a href="mailto:easyicecalabria@gmail.com" className="inline-flex items-center gap-2 h-12 px-7 rounded-lg border border-white/30 text-white hover:bg-white/10 font-medium transition-colors" data-testid="home-email-button">
                Scrivici · easyicecalabria@gmail.com
              </a>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <PartnerStat n="-25%" label="Risparmio medio sul listino" />
            <PartnerStat n="24h" label="Tempo di risposta" />
            <PartnerStat n="500+" label="Partner attivi" />
            <PartnerStat n="7/7" label="Consegna 7 giorni/7" full />
          </div>
        </div>
      </section>

      <footer className="hidden">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 flex items-center justify-between text-sm text-slate-500">
          <p>© {new Date().getFullYear()} EasyIce</p>
        </div>
      </footer>
    </div>
  );
};

const PartnerStat = ({ n, label, full }) => (
  <div className={`bg-white/10 border border-white/20 rounded-2xl p-5 backdrop-blur-sm ${full ? "col-span-3" : ""}`}>
    <p className="font-heading text-3xl font-semibold">{n}</p>
    <p className="text-xs text-sky-100/80 mt-1 leading-snug">{label}</p>
  </div>
);

const Stat = ({ n, label }) => (
  <div>
    <p className="font-heading text-3xl text-[#032B44] font-semibold">{n}</p>
    <p className="text-xs text-slate-500 mt-1">{label}</p>
  </div>
);

const Feature = ({ icon, title, desc }) => (
  <div className="flex gap-4">
    <div className="w-11 h-11 rounded-xl bg-white border border-sky-100 grid place-items-center text-sky-700 shrink-0">
      {React.cloneElement(icon, { className: "w-5 h-5" })}
    </div>
    <div>
      <h4 className="font-heading text-lg font-medium text-[#032B44]">{title}</h4>
      <p className="text-sm text-slate-600 mt-1 leading-relaxed">{desc}</p>
    </div>
  </div>
);

export default Home;
